import React from 'react';
import styled from '@emotion/styled';
import { Link } from '@reach/router';
import { COLORS, FOCUS, DOTTED_FOCUS, MAIN_CONTENT_SIZE } from '../../constants';
import { Container, Flex } from 'react-magma-dom';
import { StyledScroll } from './StyledScroll';

interface IStyledWrapper {
  children: React.ReactNode;
  color?: string;
}

interface IExemplarHeader {
  backgroundColor: string;
  letterColor: string;
}

interface ILessonTypeHeader {
  backgroundColor: string;
  letterColor: string;
}

export interface imageProps {
  width: string;
  marginRight?: string;
}

export interface imgProps {
  width: string;
  height: string;
}

export interface margin {
  margin: string;
}
interface MarginProps {
  margin: string;
}
export interface padding {
  padding: string;
}

export const StyledWrapper = styled.div`
  position: relative;
  height: 100%;
  max-width: 100%;
  margin: 0 auto;
`;

export const StyledInsideWrapper = styled.div<{
  isFullScreen: boolean;
  width?: string;
}>`
  position: relative;
  height: ${({ isFullScreen }) => (isFullScreen ? 'auto' : 'calc(100% - 67px)')};
  max-width: 100%;
  width: ${({ width }) => (width ? `${width}` : '100%')};
  margin: 0 auto;
  ${StyledScroll}
`;

export const BGWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100%;
  background: ${({ color }) => color};
  margin: 0 auto;
`;

export const StyledFullScreenWrapper = styled.div<{ isFullScreen: boolean }>`
  ${({ isFullScreen }) =>
    isFullScreen
      ? `
    position: absolute!important;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100%;
    z-index: 10;
  `
      : null}
`;

export const StyledWrapperWithBG: React.FC<IStyledWrapper> = (props): JSX.Element => {
  const { color, children } = props;

  return (
    <BGWrapper color={color}>
      <StyledWrapper>{children}</StyledWrapper>
    </BGWrapper>
  );
};

export const TextTypesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  max-width: 100%;
`;

export const ExemplarWrapper = styled.div<{ margin: string }>`
  max-width: 989px;
  color: ${COLORS.darkGray};
  font-size: 22px;
  border-radius: 4px;
  border: solid 1px #dfdfdf;
  box-sizing: border-box;
  margin: 44px auto;

  @media (max-width: 1366px) {
    max-width: 990px;
  }

  @media (max-width: 1023px) {
    max-width: 925px;
  }
`;

export const ExemplarHeaderWrapper = styled.div<IExemplarHeader>`
  padding: 21px 0px 26px 24px;
  color: ${COLORS.blackOlive};

  div {
    font-size: 28px;
    align-items: center;
    margin: 0px;
  }
`;

export const ImageWrapper = styled.span<imageProps>`
  width: ${({ width }) => width};
  padding-right: ${({ marginRight }) => (marginRight ? marginRight : 'auto')};
`;

export const ImgWrapper = styled.div<imgProps>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
  overflow: hidden;
  display: flex;
  align-items: center;
`;

export const MarginWrapper = styled.div<MarginProps>`
  margin: ${({ margin }) => margin};
`;

export const RelatedWrapper = styled.div`
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;
export const BottomNavigationWrapper = styled.div`
  background: ${COLORS.white};
  width: 100%;
  z-index: 100;
`;

export const StyledContentWrapper = styled.div<{ width: number }>`
  max-width: ${({ width }) => width}px;
  position: relative;
  margin: 0 auto;

  & > a:first-child {
    margin-top: 31px;
  }
`;

export const SecondaryNavWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 94px;
  padding-left: 72px;
`;

interface IWrapper {
  padding: string;
}

export const ContainerWrapper = styled(Container)<IWrapper>`
  display: flex;
  flex-direction: column;
  color: #000000;
  padding: ${({ padding }) => padding};
  font-size: x-large;
  width: fit-content;
  background: none;
  width: 50%;
`;

export const FlexWrapper = styled.div`
  display: flex;
`;

export const FlexColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${({ color }) => (color != undefined ? `background-color: ${color};` : '')};
`;

export const StyledNoteWrapper = styled.div<{
  bgColor: string;
}>`
  height: 24px;
  width: 192px;
  display: inline-flex;
  background: ${({ bgColor }) => bgColor};
`;

export const VerbsButtonWrapper = styled.div`
  position: absolute !important;
  bottom: 0px;
  right: 0px;
  padding-bottom: 20px;
  padding-right: 20.1px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

export const StyledNavWrapper = styled.div`
  @media (max-width: 1023px) {
    display: none;
  }
`;

export const StyledLogoWrapper = styled(Link)`
  padding-top: 20px;
  :focus {
    ${FOCUS}
  }
`;

export const WordsWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: ${MAIN_CONTENT_SIZE.M[1]}px;

  @media (max-width: 1366px) {
    min-height: ${MAIN_CONTENT_SIZE.S[1]}px;
  }

  @media (min-width: 2046px) {
    min-height: ${MAIN_CONTENT_SIZE.L[1]}px;
  }
`;

export const WordsImageWrapper = styled.div<{
  top: number;
  left: number;
  width: number;
  height?: number;
  isHidden: boolean;
}>`
  position: absolute !important;
  top: ${({ top }) => `${top}%`};
  left: ${({ left }) => `${left}%`};
  width: ${({ width }) => `${width}%`};
  ${({ height }) => (height ? `height: ${height}%;` : '')}
  background: ${({ isHidden }) => isHidden && COLORS.gray95};
  z-index: ${({ isHidden }) => (isHidden ? 10 : 30)};
  border: ${({ isHidden }) => isHidden && `1px dashed ${COLORS.blackOlive}`};

  ${({ isHidden }) =>
    isHidden &&
    `
  display: flex;
  justify-content: center;
  align-items: center;
  `}

  cursor: pointer;

  :focus {
    ${DOTTED_FOCUS}
  }

  :hover {
    box-shadow: 4px 4px 4px 0 rgba(0, 0, 0, 0.15);
  }
`;

export const ImageWithPositionWrapper = styled.div<{
  top: number;
  left: number;
  width: number;
  height?: number;
}>`
  position: absolute !important;
  top: ${({ top }) => `${top}%`};
  left: ${({ left }) => `${left}%`};
  width: ${({ width }) => `${width}%`};
  height: ${({ height }) => `${height}%`};
`;

export const AbsoluteNoteWrapper = styled.div<{ isManuallyHidden: boolean }>`
  width: 192px;
  display: ${({ isManuallyHidden }) => (isManuallyHidden ? 'none' : '')};
  position: absolute !important;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.35);
  z-index: 41;
`;

export const ToolButtonWrapper = styled.div<{
  disabled?: boolean;
  isHighlighted: boolean;
  bgColor: string;
}>`
  background: ${({ isHighlighted, bgColor }) => (isHighlighted ? bgColor : 'transparent')};
  z-index: 100;
  &:hover {
    background: ${({ bgColor }) => bgColor};
  }
  position: relative;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

export const ToolsContainer = styled.div`
  max-width: 926px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export const EditButtonsWrapper = styled.div`
  display: flex;
  position: relative;
`;

export const SaveCloseButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;

export const DivAsButtonWrapper = styled.div`
  height: 28px;
  margin: 4px;
`;

export const ToolWrapper = styled.div<{
  x: number;
  y: number;
  margin: string;
}>`
  box-shadow: 1px 1px 4px 0 rgba(0, 0, 0, 0.35);
  margin: ${({ margin }) => margin};
  left: ${({ x }) => x}px;
  top: ${({ y }) => y}px;
  width: fit-content;
  background-color: white;
  display: flex;
  position: absolute !important;
  transform: translate(-50%, 0);
  z-index: 41;

  & div {
    top: -10px;
  }

  & > svg:first-of-type {
    top: 0px;
    position: absolute !important;
    z-index: 1;
    margin-left: calc(50% - 8px);
  }
`;

export const StyledProformaButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  margin: 0 auto;

  max-width: 926px;
  padding-top: 13px;
`;

export const LessonsContainer = styled.section<{
  width?: number;
}>`
  max-width: ${({ width }) => width || 960}px;
  width: ${({ width }) => width || 960}px;
  margin: 0 auto;
  margin-top: 39px;
  padding: 0 10px;
`;

export const LessonsWrapper = styled.div`
  width: 100%;
`;

export const LessonsHeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 22px;
`;

export const LessonsHeader = styled.div`
  color: ${COLORS.neutral3};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  svg {
    margin-right: 16px;
  }
  nav {
    ol {
      li {
        a {
          ${DOTTED_FOCUS};
        }
      }
    }
  }
`;

export const LessonsSubHeader = styled.div<{
  withBorderBottom?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  font-size: 36px;
  color: ${COLORS.blackOlive};
  margin-bottom: 32px;
  ${({ withBorderBottom }) =>
    withBorderBottom &&
    `
    border-bottom: solid 1px #3f3f3f;
    padding-bottom: 6px;
    margin-bottom: 0;
  `}

  p {
    margin: 0;
  }
`;

export const LessonPlanHeader = styled.div`
  width: fit-content;
  position: relative;
  vertical-align: top;

  button {
    width: auto;
    height: auto;
    margin: 0 !important;
    position: absolute !important;
    top: 8px;
    margin-left: 8px !important;
  }
`;

export const LessonsListBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

export const LessonsTypeContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

export const LessonsTypeHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

export const LessonsTitle = styled.div`
  font-size: 24px;
  color: ${COLORS.blackOlive};
`;

export const LessonsTypeContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: row;
  flex-wrap: wrap;
  a {
    display: block;
    text-decoration: none;
  }
`;

export const StyledTypeLink = styled(Link)`
  ${DOTTED_FOCUS};
  display: block;
  position: relative;
`;

export const StyledLessonTextLink = styled(Link)`
  display: inline-block !important;
  text-decoration: underline !important;
  color: ${COLORS.white};

  &:focus {
    outline-offset: 3px !important;
    outline: 2px dotted #ffffff !important;
  }
`;

export const LessonType = styled.div<{
  color: string;
  isAccessible: boolean;
}>`
  width: 459px;
  height: 77px;
  display: flex;
  justify-content: ${({ isAccessible }) => (isAccessible ? 'center' : 'space-between')};
  align-items: center;
  padding: 0 16px;
  border-radius: 4px;
  background-color: ${({ color }) => color};

  &:hover {
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.25);
  }

  ${DOTTED_FOCUS};
`;

export const StyledEmptyBlock = styled.div`
  width: 24px;
`;

export const TooltipPointer = styled.div`
  width: 0;
  height: 0;
  border-top: 11.5px solid transparent;
  border-right: 23px solid ${COLORS.blackOlive};
  border-bottom: 11.5px solid transparent;
  position: absolute !important;
  top: 50%;
  transform: translateY(-50%);
  left: -14px;
`;

export const LessonTypeWrapper = styled.div`
  width: 100%;
  border: 1px solid #dfdfdf;
  border-radius: 4px;
`;

export const LessonTypeHeader = styled.div<ILessonTypeHeader>`
  height: 50%;
  background-color: ${({ backgroundColor }) => backgroundColor};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 24px;
  box-sizing: border-box;
  font-size: 28px;
  color: ${({ letterColor }) => letterColor};
  border-radius: 4px 4px 0px 0px;
  svg {
    width: 45px;
    height: 45px;
    margin-left: 12px;
  }
`;

export const LessonTypesContainer = styled.div``;

export const StageTypeRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

export const TextTypeRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  &:first-of-type {
    border-top: 1px solid #dfdfdf;
  }
  &:not(:last-child) {
    border-bottom: 1px solid #dfdfdf;
  }
`;

export const ExemplarsContainer = styled.div``;

export const Image = styled.img`
  width: 100%;
`;

export const StickedBottomWrapper = styled.div<{
  isFixed: boolean;
}>`
  width: 100%;
  ${({ isFixed }) =>
    isFixed &&
    `
  position: absolute!important;
  bottom: 0;
  `}
  z-index: 100;
`;

export const StyledExemplarAndNavigationWrapper = styled.div`
  position: relative;
  height: calc(100% - 76px);
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const AccordionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FontWeightWrapper = styled.span<{ weight: number }>`
  font-weight: ${({ weight }) => weight};
`;

export const StyledBorderWrapper = styled.div<{ color: string }>`
  border-top: 1px solid ${({ color }) => color};
  width: 100%;
`;

export const TooltipForLanguageWordWrapper = styled.div<{
  x: number;
  y: number;
  toolWidth: number;
}>`
  width: 194px;
  height: 52px;
  position: absolute !important;
  top: ${({ y }) => `${y + 2}px`};
  left: ${({ x, toolWidth }) => `${x - toolWidth / 2}px`};
  z-index: 100;
  & > svg {
    position: absolute !important;
    top: 10px;
    margin-left: calc(50% - 8px);
    z-index: 2;
  }
`;

export const TooltipForLanguageWordContent = styled.div`
  position: absolute !important;
  bottom: 0;
  display: flex;
  height: 42px;
  padding: 13px 12px 13px 11px;
  box-sizing: border-box;
  box-shadow:
    1px 1px 4px 0 rgba(0, 0, 0, 0.35),
    1px 1px 4px 0 rgba(0, 0, 0, 0.35);
  background: ${COLORS.white};
`;

export const StyledFilterWrapper = styled.span`
  color: ${COLORS.darkSpringGreen};
  margin: 0px 5px 0px 0px;
`;

export const StyledDeleteExplanation = styled.span`
  text-align: center;
  margin-top: 19px;
  margin-bottom: 24px;
`;

export const StyledDeleteTitle = styled.span<{ highlightColor: string }>`
  width: fit-content;
  max-width: 555px;
  font-weight: 600;
  display: block;
  padding: 0 4px;
  margin: 0 auto;
  border-radius: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background-color: ${({ highlightColor }) => highlightColor};
`;

export const StyledDeleteHeading = styled.span<{
  color?: string;
  marginBottom?: string;
}>`
  font-size: 24px;
  font-weight: bold;
  color: ${({ color }) => color || COLORS.cadmiumViolet};
  ${({ marginBottom }) => (marginBottom ? `margin-bottom: ${marginBottom};` : '')}
`;

export const StyledButtonSpinnerWrapper = styled.div<{
  margin: string;
  width?: string;
  height?: string;
  disabled?: boolean;
}>`
  width: ${({ width }) => (width ? width : '36px')};
  height: ${({ height }) => (height ? height : '36px')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${({ margin }) => margin};
  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
  `}
`;

export const StyledHiddenBlockWrapper = styled.div<{ withText: boolean }>`
  width: 109px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  svg {
    margin-bottom: ${({ withText }) => (withText ? '6.4px;' : '')};
  }
  span {
    text-align: center;
    font-size: 15px;
    font-weight: 600;
    height: 57px;
    opacity: 0.4;
    line-height: 1.3;
    width: 130px;
  }
`;

export const StyledExemplarsWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

export const StyledDefaultImagesErrorContent = styled.div`
  div:last-child {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export const StyledScrollableModalWrapper = styled.div`
  position: relative;
  width: fit-content;
  margin: 101px auto 85px;

  background: ${COLORS.white};

  & > button:first-of-type {
    position: absolute !important;
    top: 3px;
    right: 0px;
  }
`;

export const StyledTermsOfUseContent = styled.div`
  width: 863px;
  padding: 30px 46px 40px 47px;

  border: solid 1px ${COLORS.seaBlue}!important;
  box-shadow: 0 2px 9px 0 rgba(0, 0, 0, 0.2);
`;

export const StyledTermsOfUseContentBlock = styled.div`
  font-size: 16px;
  font-weight: normal;

  color: ${COLORS.black};
`;

export const StickedImageWrapper = styled.div<{ imageSide: string }>`
  display: flex;
  justify-content: ${({ imageSide }) => (imageSide === 'left' ? 'flex-start' : 'flex-end')};
`;

export const StyledEllipsisWrapper = styled(Flex)<{ width: number; radius?: number }>`
  ${({ radius }) => (radius ? `border-radius: ${radius}px;` : '')}

  max-width: ${({ width }) => width}px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledCenteredSpinnerWrapper = styled(Flex)`
  position: fixed;
  top: 50%;
`;

export const StyledExemplarName = styled.span<{ highlightColor: string }>`
  max-width: 555px;
  padding: 0 4px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: ${({ highlightColor }) => highlightColor};
`;

export const StyledIntroductionToHybridContentWrapper = styled.div`
  margin: 18px 0px 28px;
  color: ${COLORS.black};
  font-weight: 400;
  line-height: normal;
  letter-spacing: normal;
`;

export const StyledIntroductionToHybridContainer = styled.div`
  max-width: 372px;
`;
